import { useState } from 'react';
import { Icon } from './icons';
import { cn } from '../lib/utils';
import { handleTreeKeyDown } from '../lib/tree-keyboard';
import type { CategoryNode } from '../api/types';
import type { PeriodNode } from '../lib/period-tree';
import type { FolderPhaseGroup } from '../lib/folder-phases';

export interface FolderTreeProps {
  nodes: CategoryNode[];
  selectedId: string | null;
  onSelect: (node: CategoryNode | null) => void;
  /** The last levels of the folder view: per category, the Year → Month /
   * Quarter (and Undated) nodes of the documents filed directly there
   * (`periodNodesByCategory`, lib/category-tree.ts). */
  periodNodes?: ReadonlyMap<string, readonly PeriodNode[]>;
  /** The selected period node's key under `selectedId` (`y:2025`, `m:2025-03`,
   * …), or null for the whole folder. */
  selectedPeriod?: string | null;
  onSelectPeriod?: (node: CategoryNode, periodKey: string) => void;
  /** The checklist phases above the categories (`folderPhaseGroups`,
   * lib/folder-phases.ts): when given, the tree reads Phase › Category › …,
   * and `nodes` is ignored for the top level. Null / absent: categories at the
   * top, as before. */
  phases?: readonly FolderPhaseGroup[] | null;
  /** The selected phase's key (with no folder selected). */
  selectedPhase?: string | null;
  onSelectPhase?: (phase: FolderPhaseGroup) => void;
}

const ALL_ID = '__all__';
const NO_PERIODS: ReadonlyMap<string, readonly PeriodNode[]> = new Map();

/** A period row's tree key: the folder id and the period key, `@`-joined. */
const periodItemKey = (folderId: string, periodKey: string) => `${folderId}@${periodKey}`;
/** A phase row's tree key (never a category id: those are uuids). */
const PHASE_PREFIX = 'phase:';
const phaseItemKey = (phaseKey: string) => `${PHASE_PREFIX}${phaseKey}`;

function contains(node: CategoryNode, id: string): boolean {
  return node.children.some((child) => child.id === id || contains(child, id));
}

/** Rows open on first render: the top level (the phases — or, without them,
 * the top-level folders), and every folder on the way to the selection (or
 * holding the selected period, and its year), so the selection is visible.
 * Under a phase the categories start closed. */
function initialExpanded(
  nodes: CategoryNode[],
  selectedId: string | null,
  selectedPeriod: string | null,
  phases: readonly FolderPhaseGroup[] | null,
): Set<string> {
  const open = new Set<string>();
  const walk = (list: CategoryNode[], depth: number) => {
    for (const node of list) {
      if (
        (phases === null && depth < 1) ||
        (selectedId !== null && selectedId !== node.id && contains(node, selectedId)) ||
        (selectedId === node.id && selectedPeriod !== null)
      ) {
        open.add(node.id);
      }
      walk(node.children, depth + 1);
    }
  };
  if (phases) {
    for (const phase of phases) open.add(phaseItemKey(phase.key));
    walk(
      phases.flatMap((p) => p.nodes),
      0,
    );
  } else {
    walk(nodes, 0);
  }
  // A selected month / quarter: open its year too.
  const sub = selectedId && selectedPeriod ? /^[mq]:(\d{4})-/.exec(selectedPeriod) : null;
  if (selectedId && sub) open.add(periodItemKey(selectedId, `y:${sub[1]}`));
  return open;
}

const ROW =
  'flex min-h-8 cursor-pointer items-center gap-1.5 rounded-md py-1 pr-2 text-sm outline-none hover:bg-surface-2 focus-visible:ring-2 focus-visible:ring-ring max-lg:min-h-tap';

/**
 * The room's category hierarchy as a folder tree (DataRoom UI "FolderTree") — a
 * view over the curated category tag graph (ADR-0004: folders are tag views).
 * Selecting a folder filters the document list; "All documents" clears it. The
 * last levels are the PERIOD of a folder's own documents (owner asks
 * 2026-10-02 and 2026-10-12): Credit Card Statements › American Express › 2025 ›
 * January, as ordinary indented tree rows after the folder's subfolders: years
 * newest first, then Q1–Q4 and the months (spelled out, calendar order) under
 * each year, "Undated" last. Selecting a period row narrows the list to the
 * folder's own documents in it.
 *
 * Accessibility: the WAI-ARIA tree pattern — role=tree / treeitem / group with
 * aria-level, aria-expanded (branches only) and aria-selected, a roving tabindex
 * (one tab stop; the arrow keys, Home/End and type-ahead move within — see
 * handleTreeKeyDown), Enter/Space select. The chevron is pointer-only; the
 * keyboard opens and closes with → / ←.
 */
export function FolderTree({
  nodes,
  selectedId,
  onSelect,
  periodNodes = NO_PERIODS,
  selectedPeriod = null,
  onSelectPeriod,
  phases = null,
  selectedPhase = null,
  onSelectPhase,
}: FolderTreeProps) {
  const [expanded, setExpanded] = useState<Set<string>>(() =>
    initialExpanded(nodes, selectedId, selectedPeriod, phases),
  );
  const phaseSelected = selectedId === null && selectedPhase !== null && phases !== null;
  const selectedKey =
    selectedId === null
      ? phaseSelected
        ? phaseItemKey(selectedPhase)
        : ALL_ID
      : selectedPeriod
        ? periodItemKey(selectedId, selectedPeriod)
        : selectedId;
  // The roving tab stop: the last focused item, else the selection.
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const tabStop = activeKey ?? selectedKey;

  const byId = new Map<string, CategoryNode>();
  const index = (list: CategoryNode[]) => {
    for (const n of list) {
      byId.set(n.id, n);
      index(n.children);
    }
  };
  index(nodes);

  const setOpen = (id: string, open: boolean) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (open) next.add(id);
      else next.delete(id);
      return next;
    });

  const activate = (key: string) => {
    if (key === ALL_ID) return onSelect(null);
    if (key.startsWith(PHASE_PREFIX)) {
      const phase = phases?.find((p) => phaseItemKey(p.key) === key);
      if (phase) onSelectPhase?.(phase);
      return;
    }
    const at = key.indexOf('@');
    const node = byId.get(at < 0 ? key : key.slice(0, at));
    if (!node) return;
    if (at >= 0) onSelectPeriod?.(node, key.slice(at + 1));
    else onSelect(node);
  };

  const itemProps = (key: string, label: string) => ({
    role: 'treeitem' as const,
    tabIndex: key === tabStop ? 0 : -1,
    'data-key': key,
    'data-label': label,
    onFocus: () => setActiveKey(key),
    onClick: () => activate(key),
  });

  const chevron = (key: string, open: boolean) => (
    <span
      aria-hidden="true"
      className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded text-ink-3 hover:bg-surface-2"
      onClick={(e) => {
        e.stopPropagation();
        setOpen(key, !open);
      }}
    >
      <Icon name="chevronRight" className={cn('transition-transform', open && 'rotate-90')} />
    </span>
  );
  const spacer = <span aria-hidden="true" className="w-5 shrink-0" />;
  const countBadge = (count: number, selected: boolean) => (
    <span
      className={cn('shrink-0 text-xs tabular-nums', selected ? 'text-primary-ink' : 'text-ink-3')}
    >
      {count}
    </span>
  );

  // A period row (year / quarter / month / Undated) under `folder`: an ordinary
  // treeitem one level down; a year holding months or quarters is a branch.
  const renderPeriod = (folder: CategoryNode, period: PeriodNode, level: number) => {
    const key = periodItemKey(folder.id, period.key);
    const isBranch = period.children.length > 0;
    const open = isBranch && expanded.has(key);
    const selected = selectedId === folder.id && selectedPeriod === period.key;
    return (
      <li key={key} role="none">
        <div
          {...itemProps(key, period.label)}
          aria-level={level}
          aria-selected={selected}
          aria-expanded={isBranch ? open : undefined}
          aria-label={`${folder.name}, ${period.fullLabel}: ${period.count} document${period.count === 1 ? '' : 's'}`}
          data-period={period.key}
          title={`${folder.name} · ${period.fullLabel}`}
          className={cn(
            ROW,
            selected
              ? 'bg-primary-soft font-semibold text-primary-ink'
              : period.kind === 'needs_filing'
                ? 'text-st-check-fg'
                : period.kind === 'undated'
                  ? 'text-ink-2'
                  : 'text-ink',
          )}
          style={{ paddingLeft: (level - 1) * 14 + 4 }}
        >
          {isBranch ? chevron(key, open) : spacer}
          <span className="min-w-0 flex-1 truncate tabular-nums">{period.label}</span>
          {countBadge(period.count, selected)}
        </div>
        {open ? (
          <ul role="group">{period.children.map((c) => renderPeriod(folder, c, level + 1))}</ul>
        ) : null}
      </li>
    );
  };

  const renderNode = (node: CategoryNode, level: number) => {
    const periods = periodNodes.get(node.id) ?? [];
    const isBranch = node.children.length > 0 || periods.length > 0;
    const open = isBranch && expanded.has(node.id);
    const folderSelected = selectedId === node.id && selectedPeriod === null;
    return (
      <li key={node.id} role="none">
        <div
          {...itemProps(node.id, node.name)}
          aria-level={level}
          aria-selected={folderSelected}
          aria-expanded={isBranch ? open : undefined}
          title={node.name}
          className={cn(
            ROW,
            folderSelected ? 'bg-primary-soft font-semibold text-primary-ink' : 'text-ink',
          )}
          style={{ paddingLeft: (level - 1) * 14 + 4 }}
        >
          {isBranch ? chevron(node.id, open) : spacer}
          <span className="min-w-0 flex-1 truncate">{node.name}</span>
          {countBadge(node.documentCount, folderSelected)}
        </div>
        {open ? (
          <ul role="group">
            {/* Subfolders first, then the folder's own documents by period. */}
            {node.children.map((child) => renderNode(child, level + 1))}
            {periods.map((period) => renderPeriod(node, period, level + 1))}
          </ul>
        ) : null}
      </li>
    );
  };

  // A checklist phase (the spreadsheet's Column A): a branch over its top-level
  // categories, with their count; selecting it lists the documents under them.
  const renderPhase = (phase: FolderPhaseGroup) => {
    const key = phaseItemKey(phase.key);
    const open = expanded.has(key);
    const selected = phaseSelected && selectedPhase === phase.key;
    return (
      <li key={key} role="none">
        <div
          {...itemProps(key, phase.name)}
          aria-level={1}
          aria-selected={selected}
          aria-expanded={open}
          data-phase={phase.key}
          title={phase.name}
          className={cn(
            ROW,
            'pl-1 font-semibold',
            selected ? 'bg-primary-soft text-primary-ink' : 'text-ink',
          )}
        >
          {chevron(key, open)}
          <span className="min-w-0 flex-1 truncate">{phase.name}</span>
          {countBadge(phase.documentCount, selected)}
        </div>
        {open ? <ul role="group">{phase.nodes.map((node) => renderNode(node, 2))}</ul> : null}
      </li>
    );
  };

  return (
    <ul
      role="tree"
      aria-label="Folders"
      className="text-sm"
      onKeyDown={(e) =>
        handleTreeKeyDown(e, {
          setExpanded: (item, open) => setOpen(item.dataset.key ?? '', open),
          activate: (item) => activate(item.dataset.key ?? ''),
        })
      }
    >
      <li role="none">
        <div
          {...itemProps(ALL_ID, 'All documents')}
          aria-level={1}
          aria-selected={selectedId === null && !phaseSelected}
          className={cn(
            ROW,
            'pl-2 font-medium',
            selectedId === null && !phaseSelected
              ? 'bg-primary-soft font-semibold text-primary-ink'
              : 'text-ink',
          )}
        >
          <Icon name="folder" className="text-ink-3" />
          All documents
        </div>
      </li>
      {phases ? phases.map(renderPhase) : nodes.map((node) => renderNode(node, 1))}
    </ul>
  );
}
